import PropTypes from 'prop-types';
import { formatCurrency } from 'Utils/formatters';
import { DRAWER_STACK, REPLAY_BLOCK_CLASS } from 'Utils/constants';
import useLocale from 'Hooks/useLocale';
import IconInfo from 'Components/Atoms/IconInfo/IconInfo';
import { cn } from 'Utils/cn';
import OutlineCloseSmall from 'Components/Atoms/OutlineIcons/OutlineCloseSmall';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import { useTranslation } from 'react-i18next';
import useDrawerStack from 'Hooks/useDrawerStack';
import IconButton from 'Components/Atoms/IconButton/IconButton';

const VARIANTS = {
    SELECTABLE: 'selectable',
};

function CardTransactionHandle(props) {
    const { onClick, className, children } = props;

    if (onClick) {
        return (
            <button type="button" onClick={onClick} className={className}>
                {children}
            </button>
        );
    }

    return <div className={className}>{children}</div>;
}

CardTransactionHandle.propTypes = {
    onClick: PropTypes.func,
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
};

export default function CardTransaction(props) {
    const { transaction, onClick, className, onDelete, variant, deleteTooltip } = props;
    const isIncome = transaction.amount * (transaction.reverse_sign ? -1 : 1) > 0;

    const locale = useLocale();
    const { products } = useProvidersConnections();
    const { t } = useTranslation('transactions');
    const { open } = useDrawerStack();

    const hasProvider = !!transaction.provider_connection_product;

    const accountProduct = transaction.account_product_id
        ? products.find((product) => product.id === transaction.account_product_id)
        : null;

    const productName = accountProduct
        ? accountProduct.name
        : hasProvider
          ? transaction.provider_connection_product.name
          : '';

    const handleClick = () => {
        open(DRAWER_STACK.TRANSACTION_EDITOR, {
            transactionId: transaction.id,
            title: t('transaction'),
        });
    };

    return (
        <CardTransactionHandle
            onClick={onClick}
            className={cn(
                'relative group grid grid-cols-[20px_1fr_auto] gap-2 p-3 bg-white ring ring-gray-200 text-dark-blue text-left rounded-lg transition-shadow',
                variant === VARIANTS.SELECTABLE && 'hover:ring-1 cursor-pointer hover:ring-primary',
                className,
            )}
        >
            <div>
                {accountProduct ? (
                    <img
                        src={accountProduct.provider_logo_url}
                        className="h-auto w-full block"
                        height={20}
                        width={20}
                        alt=""
                    />
                ) : (
                    <>
                        {!hasProvider && <div className="rounded-full size-full bg-gray-100" />}
                        {hasProvider && (
                            <img
                                src={transaction.provider_connection_product.provider_logo_url}
                                className="h-auto w-full block"
                                height={20}
                                width={20}
                                alt=""
                            />
                        )}
                    </>
                )}
            </div>
            <div className="overflow-hidden">
                {variant === VARIANTS.SELECTABLE ? (
                    <div className="text-sm font-semibold py-0.5">{transaction.description}</div>
                ) : (
                    <div
                        tabIndex={0}
                        onClick={handleClick}
                        className="text-sm w-fit font-semibold py-0.5 cursor-pointer"
                        title={transaction.description}
                    >
                        {transaction.description}
                    </div>
                )}
                <div className="truncate opacity-50 text-xs">{productName}</div>
            </div>
            <div className="text-right flex justify-end items-center gap-2">
                <div className="text-right">
                    <div className={cn('text-sm py-0.5', isIncome && 'text-income', REPLAY_BLOCK_CLASS)}>
                        {formatCurrency(transaction.amount, { currency: transaction.currency, locale })}
                    </div>
                    <div className="truncate opacity-50 text-xs flex justify-end items-center gap-1">
                        <div>{transaction.date_booked.toLocaleDateString(locale)}</div>
                        {transaction.date_executed &&
                            transaction.date_executed.getUTCDate() !== transaction.date_booked.getUTCDate() && (
                                <IconInfo tooltip={transaction.date_executed.toLocaleDateString(locale)} />
                            )}
                    </div>
                </div>
                {onDelete && (
                    <IconButton
                        size="small"
                        icon={<OutlineCloseSmall />}
                        tooltip={deleteTooltip}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                    />
                )}
            </div>
        </CardTransactionHandle>
    );
}

CardTransaction.propTypes = {
    transaction: PropTypes.object.isRequired,
    variant: PropTypes.oneOf(Object.values(VARIANTS)),
    onClick: PropTypes.func,
    onDelete: PropTypes.func,
    className: PropTypes.string,
    deleteTooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};
